import { IHistory } from '../../interfaces';
import * as React from 'react';
import { getUser, getAllUsers } from '../../services/userService';
import { Col, Container as Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { UserModel } from '../../model/UserModel';
import { BasePage } from '../reusableComponents/BasePage';
import './dashboard.scss';
import { Button } from '../reusableComponents/FormComponents/Button';
import { Loader } from '../reusableComponents/Loader';
import { Async } from '../reusableComponents/Async';
import { ErrorPage } from '../reusableComponents/ErrorPage';
import { UserQRCode } from '../User/UserQRCode';
import { Alert } from '../Alert';
import { showAlert } from '../../utils/generalUtils';
import { getOrganizationMfa, updateOrganizationMfa } from '../../services/profileService';

export interface IProfileProps {
    history?: IHistory;
    instance: UserModel;
    userId: string;
    userUID: string;
    loggedUserAccessLevel?: string;
}

export interface IProfileState {
    errorMessage?: string;
    editProfile?: boolean;
    isOrgMfaEnabled: boolean;
    loadingMfa: boolean;
}

export class ProfileImpl extends React.Component<IProfileProps, IProfileState> {

    constructor(props: IProfileProps | Readonly<IProfileProps>) {
        super(props);
        this.state = {
            errorMessage: '',
            editProfile: false,
            isOrgMfaEnabled: false,
            loadingMfa: false
        };
    }

    promise = async () => {
        const { instance, userUID } = this.props; 
        if (!instance) {
            await getAllUsers();
        } 
        if(userUID) {
            await getUser(userUID);
        }

        const company = (instance && instance.props.company) || UserModel.getCompanyName() || 'default';
        try {
            const mfaRes: any = await getOrganizationMfa(company);
            if (mfaRes && typeof mfaRes.isMfaEnabled === 'boolean') {
                this.setState({ isOrgMfaEnabled: mfaRes.isMfaEnabled });
            }
        } catch (e) {
            console.error('Failed to load org MFA:', e);
        }
        
        return;
    }

    handleOrgMfaToggle = async () => {
        const { instance } = this.props;
        const company = (instance && instance.props.company) || UserModel.getCompanyName() || 'default';
        const newStatus = !this.state.isOrgMfaEnabled;

        this.setState({ isOrgMfaEnabled: newStatus, loadingMfa: true });
        try {
            await updateOrganizationMfa(company, newStatus);
            showAlert(
                `MFA ${newStatus ? 'Enabled' : 'Disabled'} for organization`,
                'profile-mfa-alert',
                'success-alert'
            );
        } catch (error) {
            this.setState({ isOrgMfaEnabled: !newStatus });
            showAlert(
                'Failed to update Organization MFA',
                'profile-mfa-alert',
                'danger-alert'
            );
        } finally {
            this.setState({ loadingMfa: false });
        }
    };

    /**
     * Iterated through the given profile values and renders all the profile data accordingly.
     */
    renderProfileRow = (profileKey: {} | null | undefined, value: any, key: React.Key | null | undefined) => {
        if (profileKey === 'Groups' || profileKey === 'Teams') {
            return (
                <Row key={key} className="show-grid">
                    <Col sm={6} className="key">{profileKey}</Col>
                    <Col sm={6} className="value">
                        {(value || []).map((columnValue: React.Key | null | undefined) => {
                            return (
                                <span key={columnValue}>{(columnValue || '')}<br /></span>
                            );
                        })}
                    </Col>
                </Row>
            );
        } else if (profileKey === 'Name') {
            return (
                <Row key={key} className="show-grid">
                    <Col sm={6} className="key">{profileKey}</Col>
                    <Col sm={6} className="value">
                        {value.map((name: any, index: React.Key | null | undefined) => {
                            return (
                                <span key={index}>{(name || '')}&nbsp;</span>
                            );
                        })}
                    </Col>
                </Row>
            );
        }
        return (
            <Row key={key} className="show-grid">
                <Col sm={6} className="key">{profileKey}</Col>
                <Col sm={6} className="value">{value || 'NA'}</Col>
            </Row>
        );
    }

    /**
     * To Render the profile from the instance. Calling render profile row to render the user data with rows.
     */
    renderProfile = () => {
        const { instance } = this.props;
        if (!instance) {
            return <Loader />;
        } 
        const keys = [
            'Name', 'Email', 'Phone Number', 'Access Level', 'Job Title',
            'Job Description', 'Location', 'Teams', 'Groups', 'Company',
        ];

        const { firstName, middleName, lastName, email, userId, accessLevel, signatureUrl,
            jobTitle, jobDesc, locationName, groups, projects, company, phoneNumber, userUID } = instance.props;

        const name = [firstName, middleName, lastName];
        const values = [name, email, phoneNumber, accessLevel, jobTitle, jobDesc, locationName, projects, groups, company];

        const isL2User = (accessLevel && accessLevel.toString().trim().toUpperCase() === 'L2') || 
            (this.props.loggedUserAccessLevel && this.props.loggedUserAccessLevel.toString().trim().toUpperCase() === 'L2');

        return (
            <div className="user-data">
                {isL2User && <Alert id="profile-mfa-alert" className="success-alert" style={{ marginBottom: '20px' }} />}
                {keys.map((key, index) => {
                    return this.renderProfileRow(key, values[index], index + 1.);
                })}
                {/* Organization MFA Toggle - Only visible to L2 Users */}
                {isL2User && (
                    <Row className="show-grid" style={{ alignItems: 'center' }}>
                        <Col sm={6} className="key">
                            Organization MFA:
                        </Col>
                        <Col sm={6} className="value">
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '14px' }}>
                                <label className="switch" style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    width: '44px',
                                    height: '23px',
                                    margin: 0,
                                    verticalAlign: 'middle',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={this.state.isOrgMfaEnabled}
                                        onChange={this.handleOrgMfaToggle}
                                        disabled={this.state.loadingMfa}
                                        style={{ display: 'none' }}
                                    />
                                    <span className="slider round" style={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: this.state.isOrgMfaEnabled ? '#26a65b' : '#ccc',
                                        transition: '.4s',
                                        borderRadius: '34px'
                                    }}>
                                        <span style={{
                                            position: 'absolute',
                                            content: '""',
                                            height: '17px',
                                            width: '18px',
                                            left: this.state.isOrgMfaEnabled ? '22px' : '3px',
                                            top: '3px',
                                            backgroundColor: 'white',
                                            borderRadius: '50%',
                                            transition: '.4s'
                                        }} />
                                    </span>
                                </label>
                                <span style={{
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    color: this.state.isOrgMfaEnabled ? '#26a65b' : '#e74c3c'
                                }}>
                                    {this.state.isOrgMfaEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                            </div>
                        </Col>
                    </Row>
                )}

                <Row className="show-grid">
                    <Col sm={6} className="key">Signature:</Col>
                    <Col sm={6} className="value"> {signatureUrl ?
                        <img height="56px" style={{ border: '1px solid #d2d2d2', backgroundColor: 'white'}}
                            src={signatureUrl} alt="SafeConnect"/> : <span style={{ color: 'white' }} >
                            No Signature Saved
                            </span>}</Col>
                </Row>
                <Row className="show-grid">
                    <Col sm={6} className="key">QR Code:</Col>
                    <Col sm={6} className="value"> 
                        <UserQRCode user={`${userUID}/${company}`}/> 
                    </Col>
                </Row>

                <div className="form-button">
                    <Button
                        style={{
                            color: '#FFFFFF',
                            border: 'none',
                            backgroundColor: '#26a65b'
                        }}
                        onClick={() => this.props.history?.push(`/profile/${userId}/edit`)}>
                        Update Profile
                    </Button>
                </div>
            </div>
        );
    }

    render() {
        return (
            <BasePage>
                <Grid className="profile">
                    <Async
                        identifier="UserProfile"
                        promise={this.promise}
                        loader={<Loader />}
                        error={<ErrorPage />}
                        content={this.renderProfile()}
                    />
                </Grid>
            </BasePage>
        );
    }
}

export function mapStateToProps(state: any) {
    const userId = state.login.get('userId');
    const userUID = state.login.get('userUID'); 
    const userInstance = UserModel.get(userId);  
    const loggedUserAccessLevel = (userInstance && userInstance.props && userInstance.props.accessLevel) || state.login.get('accessLevel');
    return {
        instance: userInstance,
        userId, userUID,
        loggedUserAccessLevel
    };
}

export const Profile = withRouter(connect(mapStateToProps)(ProfileImpl));
